import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./ArticleDashboard.css";
import ArticleUploadModal from "./ArticleUploadModal";
import { useAuth } from "../App";
import { apiFetch } from "../utils/apifetch";

export default function ArticleDashboard() {
  const navigate = useNavigate();
  const { hasPermission, logout } = useAuth();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("CreatedAt");
  const [sortDir, setSortDir] = useState("desc");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedArticle, setSelectedArticle] = useState(null);

  const [toastMessage, setToastMessage] = useState("");

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch("/article", {}, logout, navigate);
      const data = await res.json();
      setArticles(Array.isArray(data) ? data : []);
    } catch {
      setError("Unable to load articles");
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 5000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await apiFetch(`/article/${id}`, { method: "DELETE" }, logout, navigate);
      setArticles((a) => a.filter((x) => x.Id !== id));
      showToast("Article deleted successfully");
    } catch {
      alert("Unable to delete article");
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return articles
      .filter((a) =>
        [a.AuthorName, a.AuthorTitle, a.ShortDescription, a.CreatedAt]
          .join(" ")
          .toLowerCase()
          .includes(q),
      )
      .sort((a, b) => {
        const x = a[sortField] || "";
        const y = b[sortField] || "";
        if (x < y) return sortDir === "asc" ? -1 : 1;
        if (x > y) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
  }, [articles, search, sortField, sortDir]);

  if (loading) return <div className="profile-loading">Loading articles…</div>;
  if (error) return <div className="profile-error">{error}</div>;

  return (
    <div className="article-dashboard">
      {toastMessage && (
        <div className="toast-overlay">
          <div className="toast-success">{toastMessage}</div>
        </div>
      )}

      <div className="profile-header">
        <div>
          <h2>Article Library</h2>
          <p className="subtitle">Published Articles</p>
        </div>
        {hasPermission("article", "create") && (
          <button
            className="addButton"
            onClick={() => {
              setModalMode("add");
              setSelectedArticle(null);
              setIsModalOpen(true);
            }}
          >
            ＋ Add Article
          </button>
        )}
      </div>

      <div className="profile-toolbar">
        <input
          className="toolbar-input"
          placeholder="Search articles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="sort-group">
          <select
            className="toolbar-select"
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
          >
            <option value="AuthorName">Author</option>
            <option value="CreatedAt">Created Date</option>
          </select>
          <button
            className="toolbar-sort-btn"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </button>
        </div>
      </div>

      <div className="profile-grid">
        {filtered.map((a) => (
          <div className="profile-card" key={a.Id}>
            <div className="article-box">
              <p className="article-excerpt">Article Title</p>
              <p className="name">
                {a.ShortDescription.slice(0, 120)}
                {a.ShortDescription.length > 120 && "…"}
              </p>
            </div>
            <p className="article-excerpt">Author Title</p>
            {a.AuthorTitle && <p className="name">{a.AuthorTitle}</p>}
            <p className="article-excerpt">Author Name and Initial</p>
            <p className="name">
              {a.AuthorName}
              {a.AuthorInitial ? ` ${a.AuthorInitial}` : ""}
            </p>
            <p className="article-excerpt">Created on: </p>
            <p className="name">
              {" "}
              {a.CreatedAt
                ? new Date(a.CreatedAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "-"}
            </p>
            <div className="card-actions">
              <button onClick={() => navigate(`/article/${a.Id}`)}>View</button>
              {hasPermission("article", "update") && (
                <button
                  className="edit"
                  onClick={() => {
                    setModalMode("edit");
                    setSelectedArticle(a);
                    setIsModalOpen(true);
                  }}
                >
                  Edit
                </button>
              )}
              {hasPermission("article", "delete") && (
                <button className="danger" onClick={() => handleDelete(a.Id)}>
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="empty">No articles found</div>
        )}
      </div>

      {isModalOpen && (
        <ArticleUploadModal
          mode={modalMode}
          articleData={selectedArticle}
          onClose={() => setIsModalOpen(false)}
          onArticleUploaded={(s) => {
            setIsModalOpen(false);
            if (s === "nochange") return showToast("Article not changed");
            fetchArticles();
            showToast(
              modalMode === "edit"
                ? "Article updated successfully"
                : "Article uploaded successfully",
            );
          }}
        />
      )}
    </div>
  );
}
